import React from "react";

import cx from "classnames";

import { Button } from "../Button";
import { Icon } from "../Icon";
import { IconDictionary } from "../Icon/types";

import styles from "./IconButton.scss";

export type IconButtonProps = React.PropsWithChildren<
  React.ComponentProps<typeof Button> & {
    /** KazooIcon to display. See Icon/types.ts */
    icon: IconDictionary | (string & Record<never, never>);
    /** "regular"|"filled"|"thin" variant of the icon */
    iconVariant?: "regular" | "filled" | "thin";
    title?: string;
    ariaLabel?: string | null;
    system?: boolean;
    disabled?: boolean;
    /** Additional classes of the base element */
    className?: string | null;
    /** The size of the icon inside of the button in px width and height. */
    iconSize?: 8 | 12 | 16 | 24 | 28;
    /** Special IconButtonVariant for dark backgrounds. */
    iconButtonVariant?: "onDarkBg";
    /** Prop passed to the Button to determine button or submit. */
    type?: "button" | "submit";
    onClick?: (
      event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
    ) => void;
    draggable?: boolean;
    onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
    onDrop?: (event: React.DragEvent<HTMLElement>) => void;
    /** The number to display in the top right corner of the icon. */
    quantity?: number;

    /** Sets the size of the button container mind width and height. Default 40 */
    containerSize?: 24 | 40;
  }
>;

/**
 * A square, touchscreen friendly wrapper for a Button that includes an Icon.
 * This is built on top of the `Button` and `Icon` components. This component
 * accepts all props except `size` and `fullWidth` supported by the `Button`
 * Component, passing them through directly. The icon and size props will be
 * passed down to the icon component, however the size uses an enum to make
 * sure the icon fits into a circle shaped button.
 *
 * - This is a simple wrapper around `Button`, so it is suggested you check
 *   `Button` for any additional props / options.
 *
 * - This component no longer accepts button variants other than `transparent`.
 *   If a different variant is declared, it will be overridden by the
 *   iconButton component.
 *
 * - We must implement the `ariaLabel` and `title` props every time we use an
 *   `IconButton` for accessibility purpose, if not implemented we must update
 *   it.
 */
export function IconButton({
  children,
  className,
  iconVariant = "regular",
  icon,
  iconSize = 16,
  system,
  ariaLabel = "",
  title = "",
  iconButtonVariant,
  quantity,
  containerSize = 40,
  ...buttonProps
}: IconButtonProps) {
  const classes = cx(className, styles["icon-button"], {
    [styles["icon-button--system"]]: system,
    [styles["onDarkBg"]]: iconButtonVariant === "onDarkBg",
    [styles["compact-icon-button"]]: containerSize === 24,
  });

  const quantityClasses = cx(styles["quantity"]);
  // Make sure button props does not have a size or fullWidth as it will mess with circle
  buttonProps.size = undefined;
  buttonProps.fullWidth = false;
  // Override button variant as compromise to avoid breaking changes.
  buttonProps.variant = "transparent";
  const quantityLabel =
    quantity !== undefined && quantity > 0 ? ` (${quantity})` : "";
  return (
    <Button
      {...buttonProps}
      className={classes}
      aria-label={`${ariaLabel}${quantityLabel}`}
      title={title}
    >
      <div
        className={styles["quantity-container"]}
        data-testid="confetti-icon-button-quantity-container"
      >
        <Icon
          variant={iconVariant}
          icon={icon}
          size={iconSize}
          color="currentColor"
        />
        {!!quantity && quantity > 0 && (
          <div className={quantityClasses} data-testid="icon-quantity">
            {quantity}
          </div>
        )}
      </div>
      {children}
    </Button>
  );
}
